"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { EyeClosed, EyeOff, Check, X, CheckCircle2 } from "lucide-react"

import { useDispatch, useSelector } from "react-redux"
import { changePassword } from "../redux/slices/authSlice";
import { validatePassword, getPasswordChecks, getPasswordStrength } from "../utils/passwordValidation";
import CustomModal from "../components/Modal";

function ResetPassword() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isLoading } = useSelector((state) => state.auth)

  useEffect(() => {
    document.title = "Change Password - KAXAA Wallet"
  }, [])

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  // Use utility functions for password validation
  const passwordChecks = getPasswordChecks(newPassword, "");
  const passwordStrength = getPasswordStrength(newPassword, "");

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    // Validate new password using our utility
    const passwordError = validatePassword(newPassword, "");
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match")
      return
    }

    if (currentPassword === newPassword) {
      setError("New password must be different from current password")
      return
    }

    try {
      const response = await dispatch(changePassword({ currentPassword, newPassword })).unwrap()
      if (response.success) {
        setSuccess(true)
      } else {
        setError(response.message || "Failed to update password. Please try again.")
      }
    } catch (err) {
      setError("Failed to update password. Please try again.")
    }
  }





  return (
    <>
      <CustomModal
        isOpen={success}
        message="Your password has been updated!"
        description={<><strong>Please note:</strong> From your next login, you will need to use your new password to access your account.</>}
        primaryButtonText="Continue to Dashboard"
        secondaryButtonText="Change Another Password"
        onPrimaryClick={() => navigate("/dashboard")}
        icon={<CheckCircle2 size={32} className="text-success" />}
      />
      
      <div className="app-container">
        <div className="auth-screen flex-1" style={{ minHeight: 'auto' }}>
          <div className="auth-container">
            <div className="auth-card">
              <div className="auth-header">
                <h2 className="auth-title">Reset your password</h2>
              </div>
              <div className="reset-password-header">
                {/* <button className="back-button" onClick={() => navigate("/dashboard/settings")}>
                      ← Back to Settings
                    </button> */}
                {/* <h1 className="reset-password-title">Change Password</h1>
                    <p className="reset-password-description">Update your account password for {user?.email}</p> */}
              </div>

              <form onSubmit={handleSubmit} className="reset-password-form">
                {error && <div className="error-alert">{error}</div>}

                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <div className="password-input-container">
                    <input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="form-input form-control"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="password-input-container">
                    <input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="form-input form-control"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
                    </button>
                  </div>

                  {/* Password Requirements */}
                  <div className="password-requirements">
                    <div className="requirements-header">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="requirements-title">Password must contain:</span>
                        {newPassword.length > 0 && passwordStrength && (
                          <span 
                            className="password-strength" 
                            style={{ color: passwordStrength.color, fontWeight: '500' }}
                          >
                            {passwordStrength.text}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="requirements-list">
                      {newPassword.length === 0 ? (
                        // Static rules when no password is entered
                        <>
                          <div className="requirement-item static">
                            <span>• At least 8 characters</span>
                          </div>
                          <div className="requirement-item static">
                            <span>• One uppercase letter (A-Z)</span>
                          </div>
                          <div className="requirement-item static">
                            <span>• One lowercase letter (a-z)</span>
                          </div>
                          <div className="requirement-item static">
                            <span>• One number (0-9)</span>
                          </div>
                          <div className="requirement-item static">
                            <span>• One special character (@#$%^&*)</span>
                          </div>
                        </>
                      ) : (
                        // Real-time validation when password is being typed
                        <>
                          <div className={`requirement-item ${passwordChecks.length ? 'valid' : 'invalid'}`}>
                            {passwordChecks.length ? <Check size={14} /> : <X size={14} />}
                            <span>At least 8 characters</span>
                          </div>
                          <div className={`requirement-item ${passwordChecks.uppercase ? 'valid' : 'invalid'}`}>
                            {passwordChecks.uppercase ? <Check size={14} /> : <X size={14} />}
                            <span>One uppercase letter (A-Z)</span>
                          </div>
                          <div className={`requirement-item ${passwordChecks.lowercase ? 'valid' : 'invalid'}`}>
                            {passwordChecks.lowercase ? <Check size={14} /> : <X size={14} />}
                            <span>One lowercase letter (a-z)</span>
                          </div>
                          <div className={`requirement-item ${passwordChecks.number ? 'valid' : 'invalid'}`}>
                            {passwordChecks.number ? <Check size={14} /> : <X size={14} />}
                            <span>One number (0-9)</span>
                          </div>
                          <div className={`requirement-item ${passwordChecks.special ? 'valid' : 'invalid'}`}>
                            {passwordChecks.special ? <Check size={14} /> : <X size={14} />}
                            <span>One special character (@#$%^&*)</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="password-input-container">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="form-input form-control"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
                    </button>
                  </div>

                  {/* Password Match Indicator */}
                  {confirmPassword.length > 0 && (
                    <div className={`requirement-item ${newPassword === confirmPassword ? 'valid' : 'invalid'}`} style={{ marginTop: '8px' }}>
                      {newPassword === confirmPassword ? <Check size={14} /> : <X size={14} />}
                      <span>{newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
                    </div>
                  )}
                </div>

                {/* <div className="password-requirements">
                      <h4>Password Requirements:</h4>
                      <ul>
                        <li className={newPassword.length >= 6 ? "requirement-met" : ""}>At least 6 characters long</li>
                        <li className={/[A-Z]/.test(newPassword) ? "requirement-met" : ""}>Contains uppercase letter</li>
                        <li className={/[a-z]/.test(newPassword) ? "requirement-met" : ""}>Contains lowercase letter</li>
                        <li className={/[0-9]/.test(newPassword) ? "requirement-met" : ""}>Contains number</li>
                      </ul>
                    </div> */}

                <div className="form-actions">
                  {/* <button type="button" className="auth-button secondary" onClick={() => navigate("/dashboard/settings")}>
                        Cancel
                      </button> */}
                  <button
                    type="submit"
                    className="auth-button w-100"
                    disabled={isLoading || newPassword !== confirmPassword || validatePassword(newPassword, "")}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating Password...
                      </div>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </div>
              </form>

              {/* <div className="security-tips">
                    <h4>🔒 Security Tips:</h4>
                    <ul>
                      <li>Use a unique password that you don't use elsewhere</li>
                      <li>Consider using a password manager</li>
                      <li>Don't share your password with anyone</li>
                      <li>Change your password regularly</li>
                    </ul>
                  </div> */}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ResetPassword
